import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BanIcon,
  CheckCircle2Icon,
  MoreHorizontalIcon,
  SearchIcon,
  ShieldIcon,
  UsersIcon,
  UserXIcon,
} from "lucide-react";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/app/avatar";
import { EmptyState } from "@/components/app/empty-state";
import { Page, PageHeader } from "@/components/app/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { displayName, useMe } from "@/lib/account";
import { useFormat } from "@/lib/format";
import { PageLink } from "@/lib/pages";
import { type AdminOutputs, useAdminTRPC } from "@/lib/trpc";

type AdminUser = AdminOutputs["users"]["list"][number];

/** Everyone with an account: search, admin role, disable, delete. */
export function UsersPage() {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const [query, setQuery] = useState("");
  const deferred = useDeferredValue(query);
  const { data: users } = useQuery(trpc.users.list.queryOptions({ query: deferred }));
  const { data: me } = useMe();

  return (
    <Page>
      <PageHeader title={t("admin.users")} subtitle={t("admin.usersBody")} />
      <div className="relative mb-4 max-w-sm">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t("admin.search")}
          placeholder={t("admin.search")}
          className="pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {users && !users.length ? <EmptyState icon={UsersIcon} title={t("admin.noUsers")} /> : null}
      {users?.length ? (
        <Card className="divide-y divide-border">
          {users.map((user) => (
            <UserRow key={user.id} user={user} isMe={user.id === me?.id} />
          ))}
        </Card>
      ) : null}
    </Page>
  );
}

function StatusBadge({ status }: { status: AdminUser["status"] }) {
  const { t } = useTranslation();
  if (status === "active") {
    return (
      <Badge tone="ok">
        <CheckCircle2Icon />
        {t("userStatus.active")}
      </Badge>
    );
  }
  return status === "disabled" ? (
    <Badge tone="missing">
      <BanIcon />
      {t("userStatus.disabled")}
    </Badge>
  ) : (
    <Badge tone="neutral">
      <UserXIcon />
      {t("userStatus.deleted")}
    </Badge>
  );
}

function UserRow({ user, isMe }: { user: AdminUser; isMe: boolean }) {
  const { t } = useTranslation();
  const trpc = useAdminTRPC();
  const format = useFormat();
  const setAdmin = useMutation(trpc.users.setAdmin.mutationOptions());
  const setDisabled = useMutation(trpc.users.setDisabled.mutationOptions());
  const remove = useMutation(trpc.users.delete.mutationOptions());
  const [deleting, setDeleting] = useState(false);
  const name = user.status === "deleted" ? t("userStatus.deleted") : displayName(user);

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <Avatar user={user} className="mt-0.5 size-9" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {name}
          {isMe ? (
            <span className="font-normal text-muted-foreground"> ({t("admin.you")})</span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {user.platformRole === "admin" ? (
            <Badge tone="brand">
              <ShieldIcon />
              {t("roles.admin")}
            </Badge>
          ) : null}
          <StatusBadge status={user.status} />
          {user.garages.length ? (
            user.garages.map((g) => (
              <PageLink key={g.name} href={`/admin/garages/${g.name}`}>
                <Badge tone="outline" className="hover:bg-muted">
                  {g.name} · {t(`roles.${g.role}`)}
                  {g.status === "deactivated" ? ` · ${t("memberStatus.deactivated")}` : ""}
                </Badge>
              </PageLink>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">{t("admin.noGarage")}</span>
          )}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t("admin.lastSignIn")}:{" "}
          {user.lastLoginAt ? format.dateTime(user.lastLoginAt) : t("admin.never")} ·{" "}
          {t("admin.created")}: {format.date(user.createdAt)}
        </p>
      </div>
      {isMe || user.status === "deleted" ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t("admin.userActions", { name })}>
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() =>
                setAdmin.mutate({ userId: user.id, admin: user.platformRole !== "admin" })
              }
            >
              {user.platformRole === "admin" ? t("admin.removeAdmin") : t("admin.makeAdmin")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                setDisabled.mutate({ userId: user.id, disabled: user.status === "active" })
              }
            >
              {user.status === "active" ? t("admin.disable") : t("admin.enable")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => setDeleting(true)}
            >
              {t("admin.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t("admin.deleteTitle", { name })}
        description={t("admin.deleteBody")}
        confirmLabel={t("common.delete")}
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate({ userId: user.id }, { onSettled: () => setDeleting(false) })
        }
      />
    </div>
  );
}
